import { toast } from 'react-toastify';
import { postSocketAuthToken } from '@lib/api/v2/socket';
import {
  DOOR_RESULT_ACK_TIMEOUT_MS,
  DoorOpenResult,
  getDoorOpenMessage,
  hasDoorResultFeature,
  toDoorOpenResult,
} from '@lib/doorOpenResult';
import LOG_PREFIX from '@util/logger/constants';
import { createLogger } from '@util/logger/logger';
import io, { Socket } from 'socket.io-client';

type RefundTidRequest = number | { tid: number; isVan: boolean };
export type KioskRemoteSettings = Record<string, boolean | number | string | null | undefined | object>;

export type KioskSettingSocketResponse = {
  success?: boolean;
  error?: string;
  message?: string;
  settings?: KioskRemoteSettings;
};

export type KioskSettingRequestPayload = {
  merchantId: number;
  deviceId: string;
  location?: DeviceLocation | string;
  number?: number;
};

export type KioskSettingChangePayload = KioskSettingRequestPayload & {
  settings: KioskRemoteSettings;
};

export const REFUND_SUCCEED_STATUS = {
  FAIL: 0,
  SUCCESS: 1,
  SUCCESS_LOG_FAILED: 2, // 환불 처리 완료 (VAN/현금 방출), DB 로그 기록 실패
} as const;

export type RefundSucceedStatus = (typeof REFUND_SUCCEED_STATUS)[keyof typeof REFUND_SUCCEED_STATUS];

const getSiteLink = window.location.href;
const logger = createLogger(LOG_PREFIX.SOCKET);

// Lazy 초기화를 위한 소켓 인스턴스
let socketInstance: Socket | null = null;
// 서버가 문열기 ACK로 실제 결과를 주는지(방 가입 응답 features). LETMEUP-1200
let serverSupportsDoorResult = false;
// 가입한 가맹점 방. 재연결 시 서버가 방을 잊으므로 다시 가입해 features 응답도 되살린다(LETMEUP-1200).
const joinedRoomIds = new Set<number>();

/**
 * 소켓 URL 반환
 */
const getSocketUrl = (): string => {
  return getSiteLink.includes('localhost')
    ? (process.env.REACT_APP_SOCKET_URL_DEV as string)
    : (process.env.REACT_APP_SOCKET_URL as string);
};

// 연결 완료 후 실행할 콜백
let onConnectCallback: (() => void) | null = null;

/**
 * 소켓 연결 초기화 (로그인 후 호출)
 */
export const connectSocket = (onConnect?: () => void): Socket => {
  // 이미 연결된 경우 콜백 즉시 실행
  if (socketInstance?.connected) {
    onConnect?.();
    return socketInstance;
  }

  // 이미 인스턴스가 있지만 연결 중인 경우 콜백만 등록
  if (socketInstance) {
    if (onConnect) {
      onConnectCallback = onConnect;
    }
    return socketInstance;
  }

  // 새 소켓 인스턴스 생성
  socketInstance = io(getSocketUrl(), {
    transports: ['websocket'],
    // 연결·재연결 시마다 호출되어 소켓 인증 토큰을 새로 발급받아 부착한다 (LETMEUP-876).
    // 발급 실패·비로그인(WebCall 등)이어도 무토큰으로 연결한다 — 서버가 legacy 연결을 허용하며,
    // 토큰 만료(1h) 후 재연결 시에도 이 콜백이 다시 실행되어 새 토큰을 받는다.
    auth: (cb) => {
      postSocketAuthToken()
        .then((token) => cb(token ? { token } : {}))
        .catch(() => cb({}));
    },
  });

  // 연결 완료 시 콜백 실행
  if (onConnect) {
    onConnectCallback = onConnect;
  }

  socketInstance.on('connect', () => {
    // 재연결(또는 연결 전 가입 요청)이면 기억한 방에 다시 가입한다. 서버는 끊긴 소켓의 방을 잊는다.
    joinedRoomIds.forEach((id) => safeEmit('JoinSocketRoom', id));
    if (onConnectCallback) {
      onConnectCallback();
      onConnectCallback = null;
    }
  });

  // 방 가입 응답의 서버 기능으로 문열림 결과 ACK 지원 여부를 안다(LETMEUP-1200).
  socketInstance.on('JoinSocketRoomSuccess', (payload: unknown) => {
    serverSupportsDoorResult = hasDoorResultFeature(payload);
  });

  socketInstance.on('disconnect', () => {
    serverSupportsDoorResult = false;
  });

  socketInstance.on('connect_error', () => {});

  return socketInstance;
};

/**
 * 소켓 연결 해제 (로그아웃 시 호출)
 */
export const disconnectSocket = (): void => {
  if (socketInstance) {
    socketInstance.disconnect();
    socketInstance = null;
    serverSupportsDoorResult = false;
    joinedRoomIds.clear();
  }
};

/**
 * 소켓 인스턴스 반환 (연결되어 있지 않으면 null)
 */
export const getSocket = (): Socket | null => {
  return socketInstance;
};

/**
 * 소켓이 연결되어 있는지 확인
 */
export const isSocketConnected = (): boolean => {
  return socketInstance?.connected ?? false;
};

/**
 * 소켓 함수 호출(emit) 로그를 출력하는 헬퍼 함수
 */
const logSocketEmit = (functionName: string, params?: Record<string, any>) => {
  if (params) {
    logger.info(`${functionName} 실행`, params);
  } else {
    logger.info(`${functionName} 실행`);
  }
};

/**
 * 소켓 메시지 수신(on) 로그를 출력하는 헬퍼 함수
 */
export const logSocketReceive = (eventName: string, data?: Record<string, any>) => {
  if (data) {
    logger.info(`${eventName} 수신`, data);
  } else {
    logger.info(`${eventName} 수신`);
  }
};

/**
 * 소켓 emit 실행 (연결 확인 포함)
 */
const safeEmit = (event: string, ...args: any[]): boolean => {
  if (!socketInstance?.connected) {
    logger.warn(`소켓 이벤트 (${event}) 전송 실패: 연결되지 않음`);
    return false;
  }
  socketInstance.emit(event, ...args);
  return true;
};

// 키오스크가 응답하지 않을 때 무한 로딩을 막기 위한 클라이언트 타임아웃(ms).
// 소켓 서버의 릴레이 타임아웃(10초)보다 길게 잡아 서버측 에러 응답이 먼저 오도록 한다.
const EMIT_ACK_TIMEOUT_MS = 12000;

const safeEmitWithAck = <TResponse>(
  event: string,
  payload: Record<string, any>,
  ack: (response: TResponse) => void,
  timeoutMs: number = EMIT_ACK_TIMEOUT_MS,
): boolean => {
  if (!socketInstance?.connected) {
    logger.warn(`소켓 이벤트 (${event}) 전송 실패: 연결되지 않음`);
    ack({ success: false, error: '소켓이 연결되어 있지 않습니다.' } as TResponse);
    return false;
  }

  // ack가 서버 응답 또는 타임아웃 중 한 번만 불리도록 보장
  let settled = false;
  const timer = setTimeout(() => {
    if (settled) return;
    settled = true;
    logger.warn(`소켓 이벤트 (${event}) 응답 타임아웃: ${timeoutMs}ms`);
    ack({
      success: false,
      error: '키오스크가 응답하지 않습니다. 키오스크 전원과 네트워크 연결 상태를 확인해주세요.',
    } as TResponse);
  }, timeoutMs);

  socketInstance.emit(event, payload, (response: TResponse) => {
    if (settled) return;
    settled = true;
    clearTimeout(timer);
    ack(response);
  });
  return true;
};

export const joinRoom = (id: number) => {
  // logSocketEmit('joinRoom', { id });
  joinedRoomIds.add(id);
  safeEmit('JoinSocketRoom', id);
};

// 서버가 문열기 ACK로 실제 결과를 주면(door_result_v1) 결과를 기다려 알린다(LETMEUP-1200).
// 구버전 서버·미연결이면 기존처럼 전송만 한다. onResult 기본값은 화면 안내(토스트).
const emitDoorOpen = (event: string, args: any[], onResult: (result: DoorOpenResult) => void): boolean => {
  if (!serverSupportsDoorResult || !socketInstance?.connected) {
    return safeEmit(event, ...args);
  }
  socketInstance.timeout(DOOR_RESULT_ACK_TIMEOUT_MS).emit(event, ...args, (error: Error | null, response: unknown) => {
    const result: DoorOpenResult = error ? { status: 'unverified', code: 'ACK_TIMEOUT' } : toDoorOpenResult(response);
    logger.info(`${event} 결과`, result);
    onResult(result);
  });
  return true;
};

export const notifyDoorOpenResult = (result: DoorOpenResult) => {
  const { level, text } = getDoorOpenMessage(result);
  toast[level](text);
};

export const openDoor = (id: number, onResult: (result: DoorOpenResult) => void = notifyDoorOpenResult) => {
  logSocketEmit('openDoor', { id });
  emitDoorOpen('request_openDoor', [id], onResult);
};

export const openDoorWithLocation = (
  id: number,
  number: number,
  location: string,
  onResult: (result: DoorOpenResult) => void = notifyDoorOpenResult,
) => {
  logSocketEmit('openDoorWithLocation', { id, number, location });
  emitDoorOpen('request_openDoor_location', [id, { number, location }], onResult);
};

export const allOpenDoor = (id: number) => {
  logSocketEmit('allOpenDoor', { id });
  safeEmit('request_AllopenDoor', id);
};

export const payCancel = (id: number, tid: RefundTidRequest) => {
  logSocketEmit('payCancel', { id, tid });
  return safeEmit('refund_Product', id, tid);
};

export const payCancelInner = (id: number, tid: RefundTidRequest) => {
  logSocketEmit('payCancelInner', { id, tid });
  return safeEmit('refund_product_inner', id, tid);
};

/**
 * 환불 요청 (대상 위치 정보 포함)
 * @param merchantId
 * @param tid
 * @param location
 * @param number
 * @returns
 */
export const payCancelWithLocation = (
  merchantId: number,
  transactionId: number,
  isVan: boolean,
  location: string,
  number: number,
  refundCapability: string,
) => {
  logSocketEmit('payCancelWithLocation', {
    merchantId,
    transactionId,
    isVan,
    location,
    number,
  });
  return safeEmit('request_refund_location', merchantId, {
    merchantId,
    transactionId,
    isVan,
    location,
    number,
    refundCapability,
  });
};

// LETMEUP-1217: 원격 환불 요청은 저장되지 않고 소켓으로 한 번 전달된다. 키오스크가 꺼져 있거나
// 구버전이라 결과를 보내지 않으면 화면에 아무 변화가 없어, 일정 시간 결과가 없을 때 안내한다.
export const REFUND_RESPONSE_TIMEOUT_MS = 90 * 1000;

/**
 * 환불 결과(refundSuccess) 대기 감시. 시간 안에 결과가 오면 아무것도 하지 않고,
 * 오지 않으면 onTimeout 을 한 번 호출한다. 반환 함수로 감시를 취소한다.
 */
export const watchRefundResponse = (
  onTimeout: () => void,
  timeoutMs: number = REFUND_RESPONSE_TIMEOUT_MS,
) => {
  const socket = socketInstance;
  let timer: ReturnType<typeof setTimeout> | null = null;
  const stop = () => {
    if (timer) clearTimeout(timer);
    timer = null;
    socket?.off('refundSuccess', stop);
  };
  socket?.on('refundSuccess', stop);
  timer = setTimeout(() => {
    stop();
    onTimeout();
  }, timeoutMs);
  return stop;
};

export const recetipReprint = (id: number, rid: number) => {
  logSocketEmit('recetipReprint', { id, rid });
  safeEmit('reprint_Product', id, rid);
};

export const pRestart = (id: number) => {
  logSocketEmit('pRestart', { id });
  safeEmit('restart_program', id);
};

export const kRestart = (id: number) => {
  logSocketEmit('kRestart', { id });
  safeEmit('restart_kiosk', id);
};

export const innerPRestart = (id: number) => {
  logSocketEmit('innerPRestart', { id });
  safeEmit('inner_restart_program', id);
};

export const innerKRestart = (id: number) => {
  logSocketEmit('innerKRestart', { id });
  safeEmit('inner_restart_kiosk', id);
};

export const sendQrCode = (merchant: number, phoneNum: string, code: string) => {
  logSocketEmit('sendQrCode', { merchant, phoneNum, code });
  safeEmit('qr_print_from_backoffice', merchant, phoneNum, code);
};

// id: merchant, num: 자판기 아이템 숫자
export const vendingMachine = (id: number, num: number) => {
  logSocketEmit('vendingMachine', { id, num });
  safeEmit('request_vendingMachine', id, num);
};

export const healthCheckSocket = () => {
  logSocketEmit('healthCheckSocket');
  safeEmit('req_socket_connect');
};

// 멀티키오스크에 따른 문열림, 재시작
// 문열림
export const multiOpenDoor = (id: number, floor: number) => {
  logSocketEmit('multiOpenDoor', { id, floor });
  safeEmit('request_openDoor_inner', id, floor);
};

// PC 재시작
export const multiPcRestart = (id: number, floor: number) => {
  logSocketEmit('multiPcRestart', { id, floor });
  safeEmit('inner_restart_kiosk_Floor', id, floor);
};

// 프로그램 재시작
export const multiProgramRestart = (id: number, floor: number) => {
  logSocketEmit('multiProgramRestart', { id, floor });
  safeEmit('inner_restart_program_Floor', id, floor);
};

// 프로그램 업데이트
export const merchantUpdateKiosk = () => {
  logSocketEmit('merchantUpdateKiosk');
  return safeEmit('request_AllupdateKiosk');
};

export const updateMerchantKioskV2 = () => {
  logSocketEmit('updateMerchantKioskV2');
  return safeEmit('request_update_kiosk_all_v2');
};

export const outerUpdate = (id: number, targetVersion?: string) => {
  logSocketEmit('outerUpdate', { id, targetVersion });
  // targetVersion 지정 시 특정버전 설치/다운그레이드 (LETMEUP-461). 미지정 시 최신 업데이트.
  safeEmit('request_updateKiosk_outer', id, targetVersion ? { targetVersion } : undefined);
};

export const innerUpdate = (id: number, targetVersion?: string) => {
  logSocketEmit('innerUpdate', { id, targetVersion });
  safeEmit('request_updateKiosk_inner', id, targetVersion ? { targetVersion } : undefined);
};

/**
 * PC 재시작 (대상 위치 정보 포함)
 * @param merchantId
 * @param number
 * @param location
 */
export const restartPCWithLocation = (merchantId: number, number: number, location: string) => {
  logSocketEmit('restartPCWithLocation', { merchantId, number, location });
  safeEmit('request_restart_kiosk_location', merchantId, { number, location });
};

/**
 * 프로그램 재시작 (대상 위치 정보 포함)
 * @param merchantId
 * @param number
 * @param location
 */
export const restartProgramWithLocation = (
  merchantId: number,
  number: number,
  location: string,
) => {
  logSocketEmit('restartProgramWithLocation', { merchantId, number, location });
  safeEmit('request_restart_program_location', merchantId, { number, location });
};

/**
 * 키오스크 업데이트 (대상 위치 정보 포함)
 * @param merchantId
 * @param number
 * @param location
 */
export type KioskUpdateReceipt = {
  success: boolean;
  status: 'received' | 'not_sent' | 'unconfirmed';
  requiresInteraction?: boolean;
  error?: string;
};

export const updateKioskWithLocation = (
  merchantId: number,
  number: number,
  location: string,
  targetVersion?: string,
) => {
  logSocketEmit('updateKioskWithLocation', { merchantId, number, location, targetVersion });
  return new Promise<KioskUpdateReceipt>((resolve) => {
    if (!socketInstance?.connected) {
      resolve({ success: false, status: 'not_sent', error: '서버에 연결되지 않아 요청을 보내지 못했습니다.' });
      return;
    }
    let settled = false;
    const finish = (result: KioskUpdateReceipt) => {
      if (settled) return;
      settled = true;
      clearTimeout(timer);
      resolve(result);
    };
    const timer = setTimeout(() => finish({ success: false, status: 'unconfirmed',
      error: '기기 수신을 확인하지 못했습니다. 설치 여부 확인 전 다시 요청하지 마세요.' }), EMIT_ACK_TIMEOUT_MS);
    socketInstance.emit('request_update_kiosk_location', merchantId, {
      number, location, ...(targetVersion ? { targetVersion } : {}),
    }, (response: KioskUpdateReceipt) => {
      if (response?.status === 'received' && response.success === true) finish(response);
      else finish({ success: false, status: 'unconfirmed', error: response?.error || '기기 수신을 확인하지 못했습니다.' });
    });
  });
};

export const requestKioskSettingInfo = (
  payload: KioskSettingRequestPayload,
  callback: (response: KioskSettingSocketResponse) => void,
) => {
  logSocketEmit('requestKioskSettingInfo', payload);
  return safeEmitWithAck<KioskSettingSocketResponse>('request_kiosk_setting_info', payload, callback);
};

export const changeKioskSetting = (
  payload: KioskSettingChangePayload,
  callback: (response: KioskSettingSocketResponse) => void,
) => {
  logSocketEmit('changeKioskSetting', {
    merchantId: payload.merchantId,
    deviceId: payload.deviceId,
    location: payload.location,
    number: payload.number,
  });
  return safeEmitWithAck<KioskSettingSocketResponse>('request_kiosk_setting_change', payload, callback);
};
